import {
  type UploadCategory,
  categoriesFromLegacyExtensions,
  extensionsForCategories,
} from '@payaca/uploads';
import { assertUnreachable, isNullish } from '@payaca/utilities/guards';
import type { ErrorObject, ValidateFunction } from 'ajv';
import { ajv } from './ajv';
import { MAX_FIELDSET_DEPTH } from './constants';
import type { EqualityOptionsByType } from './equality';
import { stringEquals } from './equality';
import type {
  CustomFieldDefinitionInput,
  CustomFieldHelperType,
  CustomFieldType,
  FieldsetCustomFieldDefinitionInput,
  MultiSelectCustomFieldDefinitionInput,
  MultiSelectDisplayMode,
  Schema,
  SelectCustomFieldDefinitionInput,
  SimpleCustomFieldDefinitionInput,
  UploadCustomFieldDefinitionInput,
} from './types';
export * from './date';
export * from './equality';
export * from './type-guards';
export * from './types';

/**
 * Build a variable name in the colon-and-bracket form used by automation action
 * bodies and template placeholders. For prefix `project.customField.value` and
 * path `['solarLeadForm', 'fieldset', 'sub']` this returns
 * `project.customField.value:solarLeadForm[fieldset][sub]`.
 *
 * The single source of truth for the token format, shared by the server and the
 * web app authoring UI so the two cannot drift.
 */
export const buildCustomFieldVariableName = <Prefix extends string>(
  namePrefix: Prefix,
  path: [string, ...string[]]
): `${Prefix}:${string}` => {
  const [root, ...segments] = path;
  const bracketPart = segments.map((segment) => `[${segment}]`).join('');
  return `${namePrefix}:${root}${bracketPart}`;
};

/** Union of all concrete custom field definition types */
export type AnyCustomFieldDefinition =
  | BooleanCustomFieldDefinition
  | DateCustomFieldDefinition
  | EmailCustomFieldDefinition
  | FieldsetCustomFieldDefinition
  | MultiSelectCustomFieldDefinition
  | NumberCustomFieldDefinition
  | SelectCustomFieldDefinition
  | TextareaCustomFieldDefinition
  | TextCustomFieldDefinition
  | UploadCustomFieldDefinition
  | UrlCustomFieldDefinition
  | UserCustomFieldDefinition;

export type ChildWithPath = {
  definition: AnyCustomFieldDefinition;
  /** Full path segments including root fieldset identifier, e.g., ['rootFieldset', 'childField'] or ['rootFieldset', 'nestedFieldset', 'childField'] */
  path: string[];
};

export const CUSTOM_FIELD_TYPE_READABLE_NAME_MAP: Record<
  CustomFieldType,
  string
> = {
  textarea: 'Textarea',
  text: 'Text',
  email: 'Email',
  select: 'Select',
  ['multi-select']: 'Multi-select',
  boolean: 'True/false',
  fieldset: 'Fieldset',
  number: 'Number',
  date: 'Date',
  url: 'URL',
  upload: 'File upload',
  user: 'User',
};

/**
 * Surfaces the offending property name and its location for keywords where Ajv's
 * default message is too opaque to be actionable.
 */
const formatAjvError = (error: ErrorObject): string => {
  const baseMessage = error.message || '';
  const locationSuffix = error.instancePath
    ? ` at '${error.instancePath}'`
    : '';

  switch (error.keyword) {
    case 'additionalProperties': {
      const additionalProperty = (
        error.params as { additionalProperty?: string }
      ).additionalProperty;
      if (additionalProperty) {
        return `unrecognised property '${additionalProperty}'${locationSuffix}`;
      }
      return `${baseMessage}${locationSuffix}`;
    }
    case 'required': {
      const missingProperty = (error.params as { missingProperty?: string })
        .missingProperty;
      if (missingProperty) {
        return `missing property '${missingProperty}'${locationSuffix}`;
      }
      return `${baseMessage}${locationSuffix}`;
    }
    default:
      return `${baseMessage}${locationSuffix}`;
  }
};

export abstract class CustomFieldDefinition<
  T extends CustomFieldType = CustomFieldType,
> {
  static fromInput(input: CustomFieldDefinitionInput) {
    switch (input.type) {
      case 'text':
        return TextCustomFieldDefinition.fromInput(input);
      case 'textarea':
        return TextareaCustomFieldDefinition.fromInput(input);
      case 'email':
        return EmailCustomFieldDefinition.fromInput(input);
      case 'select':
        return SelectCustomFieldDefinition.fromInput(input);
      case 'multi-select':
        return MultiSelectCustomFieldDefinition.fromInput(input);
      case 'boolean':
        return BooleanCustomFieldDefinition.fromInput(input);
      case 'number':
        return NumberCustomFieldDefinition.fromInput(input);
      case 'date':
        return DateCustomFieldDefinition.fromInput(input);
      case 'url':
        return UrlCustomFieldDefinition.fromInput(input);
      case 'fieldset':
        return FieldsetCustomFieldDefinition.fromInput(input);
      case 'upload':
        return UploadCustomFieldDefinition.fromInput(input);
      case 'user':
        return UserCustomFieldDefinition.fromInput(input);
      default:
        throw new Error('Unreachable');
    }
  }

  static fromSchema(schema: Schema) {
    switch (schema.metadata['payaca:field-type']) {
      case 'text':
        return new TextCustomFieldDefinition(schema);
      case 'textarea':
        return new TextareaCustomFieldDefinition(schema);
      case 'email':
        return new EmailCustomFieldDefinition(schema);
      case 'select':
        return new SelectCustomFieldDefinition(schema);
      case 'multi-select':
        return new MultiSelectCustomFieldDefinition(schema);
      case 'boolean':
        return new BooleanCustomFieldDefinition(schema);
      case 'number':
        return new NumberCustomFieldDefinition(schema);
      case 'date':
        return new DateCustomFieldDefinition(schema);
      case 'url':
        return new UrlCustomFieldDefinition(schema);
      case 'fieldset':
        return new FieldsetCustomFieldDefinition(schema);
      case 'upload':
        return new UploadCustomFieldDefinition(schema);
      case 'user':
        return new UserCustomFieldDefinition(schema);
      default:
        assertUnreachable(schema.metadata['payaca:field-type']);
    }

    throw new Error('Unreachable');
  }

  readonly schema: Schema;
  readonly type: T;
  protected validator: ValidateFunction | null;
  protected formValidator: ValidateFunction | null;

  constructor(schema: Schema) {
    this.schema = schema;
    this.type = schema.metadata['payaca:field-type'] as T;
    this.validator = null;
    this.formValidator = null;
  }

  abstract getExampleValue(): CustomFieldHelperType[T]['value'];

  /**
   * Strict-by-default equality between two values of this field's type.
   * `null === null` is true; `null` versus any other value (including `""`)
   * is false. `text`, `textarea`, and `email` accept `{ caseSensitive?, trim? }`.
   * Throws on `date`, `url`, `upload`, and `fieldset` — semantics for
   * these types will land alongside the first concrete caller.
   */
  abstract equals(
    a: CustomFieldHelperType[T]['value'] | null,
    b: CustomFieldHelperType[T]['value'] | null,
    options?: EqualityOptionsByType[T]
  ): boolean;

  protected static getSharedSchemaPropertiesFromInput(
    input: CustomFieldDefinitionInput
  ) {
    return {
      metadata: {
        ['payaca:label']: input.label,
        ['payaca:field-type']: input.type,
        ['payaca:identifier']: input.identifier,
        ['payaca:description']: input.description,
      },
      ['$schema']: 'http://json-schema.org/draft/2020-12/schema',
    };
  }

  public validateValue(value: any): {
    isValid: boolean;
    validationMessages: string[];
  } {
    if (!this.validator) {
      const { $schema, ...schema } = this.schema;
      this.validator = ajv.compile(schema);
    }
    const isValid = this.validator(value);

    return {
      isValid,
      validationMessages: this.validator.errors?.map(formatAjvError) ?? [],
    };
  }

  typecheckValue(value: any): value is CustomFieldHelperType[T]['value'] {
    return this.validateValue(value).isValid;
  }

  protected abstract defaultFormatter(
    value: CustomFieldHelperType[T]['value']
  ): string;

  formatValue(
    value: unknown,
    customFormatter?: (value: CustomFieldHelperType[T]['value']) => string
  ): string | null {
    if (this.typecheckValue(value)) {
      if (value === null) return null;
      if (customFormatter) {
        return customFormatter(value);
      } else {
        return this.defaultFormatter(value);
      }
    } else {
      return null;
    }
  }

  get label(): string {
    return this.schema?.metadata?.['payaca:label'];
  }

  set label(value: string) {
    this.schema.metadata['payaca:label'] = value;
  }

  get identifier(): string {
    return this.schema?.metadata?.['payaca:identifier'];
  }

  get sortIndex(): number | undefined {
    return this.schema?.metadata?.['payaca:sort-index'];
  }

  get description(): string | undefined {
    return this.schema?.metadata?.['payaca:description'];
  }

  set description(value: string | undefined) {
    this.schema.metadata['payaca:description'] = value;
  }

  public get isRequired(): boolean {
    return !(
      Array.isArray(this.schema.type) && this.schema.type.includes('null')
    );
  }

  public set isRequired(value: boolean) {
    if (value) {
      if (Array.isArray(this.schema.type)) {
        this.schema.type = this.schema.type.filter((x) => x !== 'null');
      }
    } else {
      if (Array.isArray(this.schema.type)) {
        this.schema.type = ['null', ...this.schema.type];
      } else {
        this.schema.type = ['null', this.schema.type];
      }

      this.schema.type = ['null'];
    }
  }
}
export class TextCustomFieldDefinition extends CustomFieldDefinition<'text'> {
  getExampleValue() {
    return 'Lorem Ipsum';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'text') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
    };

    return new TextCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['text']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['text']['value'] | null,
    b: CustomFieldHelperType['text']['value'] | null,
    options?: EqualityOptionsByType['text']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class TextareaCustomFieldDefinition extends CustomFieldDefinition<'textarea'> {
  getExampleValue() {
    return 'Lorem Ipsum';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'textarea') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
    };

    return new TextareaCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['textarea']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['textarea']['value'] | null,
    b: CustomFieldHelperType['textarea']['value'] | null,
    options?: EqualityOptionsByType['textarea']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class EmailCustomFieldDefinition extends CustomFieldDefinition<'email'> {
  getExampleValue() {
    return 'example@email.com';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'email') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
      format: 'email',
    };

    return new EmailCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['email']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['email']['value'] | null,
    b: CustomFieldHelperType['email']['value'] | null,
    options?: EqualityOptionsByType['email']
  ): boolean {
    return stringEquals(a, b, options);
  }
}

export class SelectCustomFieldDefinition extends CustomFieldDefinition<'select'> {
  getExampleValue() {
    return this.options.filter((x) => x)[0] || 'selectable option';
  }

  static fromInput(input: SelectCustomFieldDefinitionInput) {
    if (input.type !== 'select') throw new Error('Invalid type');

    const options: (string | null)[] | undefined = input.options;

    if (options && !input.isRequired) {
      options.push(null);
    }

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
      enum: options,
    };

    if (input.displayMode) {
      schema.metadata['payaca:display-mode'] = input.displayMode;
    }

    return new SelectCustomFieldDefinition(schema);
  }

  get displayMode(): 'dropdown' | 'radio' {
    return this.schema.metadata['payaca:display-mode'] === 'radio'
      ? 'radio'
      : 'dropdown';
  }

  set displayMode(value: 'dropdown' | 'radio') {
    this.schema.metadata['payaca:display-mode'] = value;
  }

  get options(): (string | null)[] {
    return this.schema.enum as (string | null)[];
  }

  set options(value: string[]) {
    if (this.isRequired) {
      this.schema.enum = value;
    } else {
      this.schema.enum = [null, ...value];
    }
  }

  set isRequired(value: boolean) {
    super.isRequired = value;

    if (value) {
      this.schema.enum = this.schema.enum?.filter((x) => x !== null);
    } else {
      this.schema.enum = [null, ...(this.schema.enum || [])];
    }
  }

  defaultFormatter(value: CustomFieldHelperType['select']['value']) {
    return value;
  }

  equals(
    a: CustomFieldHelperType['select']['value'] | null,
    b: CustomFieldHelperType['select']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class MultiSelectCustomFieldDefinition extends CustomFieldDefinition<'multi-select'> {
  getExampleValue() {
    const options = this.options;

    // Two options where possible: a single-element example exercises neither a
    // separator nor the multi-value shape.
    return options.length
      ? options.slice(0, 2)
      : ['an option', 'another option'];
  }

  static fromInput(input: MultiSelectCustomFieldDefinitionInput) {
    if (input.type !== 'multi-select') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'array' : ['array', 'null'],
      items: { enum: input.options },
      // Always 1, even when optional: "nothing chosen" is recorded as no value
      // at all, so an empty array is not a representable state.
      minItems: 1,
      maxItems: input.limit ?? input.options.length,
      uniqueItems: true,
    };

    if (input.displayMode) {
      schema.metadata['payaca:display-mode'] = input.displayMode;
    }

    return new MultiSelectCustomFieldDefinition(schema);
  }

  get displayMode(): MultiSelectDisplayMode {
    return this.schema.metadata['payaca:display-mode'] === 'checkbox'
      ? 'checkbox'
      : 'dropdown';
  }

  set displayMode(value: MultiSelectDisplayMode) {
    this.schema.metadata['payaca:display-mode'] = value;
  }

  /**
   * Options live at `items.enum` rather than the top-level `enum` that
   * `select` uses, and are never padded with `null` — an absent answer is
   * expressed by the value being `null`, not by `null` being a choosable
   * option.
   */
  get options(): string[] {
    const items = this.schema.items as { enum?: string[] } | undefined;

    return items?.enum ?? [];
  }

  /** Most options that may be chosen. */
  get limit(): number {
    const maxItems = this.schema.maxItems;

    return isNullish(maxItems) ? this.options.length : (maxItems as number);
  }

  defaultFormatter(value: CustomFieldHelperType['multi-select']['value']) {
    return value.join(', ');
  }

  equals(
    _a: CustomFieldHelperType['multi-select']['value'] | null,
    _b: CustomFieldHelperType['multi-select']['value'] | null
  ): boolean {
    // Comparing a set of answers to a single expected answer has no clear
    // meaning, and OPERATORS_BY_FIELD_TYPE keeps multi-select out of the guard
    // layer's equality-capable set.
    throw new Error(
      'MultiSelectCustomFieldDefinition#equals is not implemented; multi-select values are compared by presence, not equality'
    );
  }
}

export class BooleanCustomFieldDefinition extends CustomFieldDefinition<'boolean'> {
  getExampleValue(): CustomFieldHelperType['boolean']['value'] {
    return true;
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'boolean') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'boolean' : ['boolean', 'null'],
    };

    return new BooleanCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['boolean']['value']) {
    return value ? 'True' : 'False';
  }

  equals(
    a: CustomFieldHelperType['boolean']['value'] | null,
    b: CustomFieldHelperType['boolean']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class NumberCustomFieldDefinition extends CustomFieldDefinition<'number'> {
  getExampleValue() {
    return 100;
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'number') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'number' : ['number', 'null'],
    };

    return new NumberCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['number']['value']) {
    return value.toString();
  }

  equals(
    a: CustomFieldHelperType['number']['value'] | null,
    b: CustomFieldHelperType['number']['value'] | null
  ): boolean {
    return a === b;
  }
}

export class DateCustomFieldDefinition extends CustomFieldDefinition<'date'> {
  getExampleValue() {
    return '2024-01-15';
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'date') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'string' : ['string', 'null'],
      format: 'date',
    };

    return new DateCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['date']['value']) {
    return value;
  }

  equals(
    _a: CustomFieldHelperType['date']['value'] | null,
    _b: CustomFieldHelperType['date']['value'] | null
  ): boolean {
    throw new Error(
      'DateCustomFieldDefinition#equals is not implemented; date equality semantics will land alongside the first concrete caller'
    );
  }
}

export class UrlCustomFieldDefinition extends CustomFieldDefinition<'url'> {
  getExampleValue() {
    return {
      title: 'Payaca',
      url: 'https://payaca.com/',
    };
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'url') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties: {
        title: {
          type: ['string', 'null'],
        },
        url: {
          type: 'string',
          minLength: 1,
        },
      },
      required: ['url'],
    };

    return new UrlCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['url']['value']) {
    if (value.title?.length) {
      return `${value.title} (${value.url})`;
    }

    return value.url;
  }

  validateValue(value: any) {
    const res = super.validateValue(value);

    if (!res.isValid) {
      return res;
    }

    if (value === null) {
      return {
        isValid: true,
        validationMessages: [],
      };
    }

    if (value.url) {
      try {
        new URL(value.url);
      } catch (e) {
        return {
          isValid: false,
          validationMessages: ['Invalid URL'],
        };
      }
    }

    return {
      isValid: true,
      validationMessages: [],
    };
  }

  equals(
    _a: CustomFieldHelperType['url']['value'] | null,
    _b: CustomFieldHelperType['url']['value'] | null
  ): boolean {
    throw new Error(
      'UrlCustomFieldDefinition#equals is not implemented; URL equality semantics will land alongside the first concrete caller'
    );
  }
}

// Sort children by sort-order; children with undefined sort-order values come last
export const sortBySortOrder = (a: Schema, b: Schema) => {
  if (a.metadata['payaca:sort-index'] === undefined) {
    return 1;
  }
  if (b.metadata['payaca:sort-index'] === undefined) {
    return -1;
  }
  return a.metadata['payaca:sort-index'] < b.metadata['payaca:sort-index']
    ? -1
    : 1;
};

export class FieldsetCustomFieldDefinition extends CustomFieldDefinition<'fieldset'> {
  getExampleValue(): CustomFieldHelperType['fieldset']['value'] {
    const result: CustomFieldHelperType['fieldset']['value'] = {};
    this.children.forEach((child) => {
      // Nested fieldsets return a structurally compatible value type, but TS
      // cannot verify the depth constraint.
      result[child.identifier] = child.getExampleValue();
    });

    return result;
  }

  constructor(schema: Schema) {
    // If provided a schema non-contiguous or undefined sort orders on children, set them to be contiguous
    const childSchemas: Schema[] = Object.values(
      schema?.properties || {}
    ).filter((x) => x.metadata && x.type) as Schema[];

    const sortedChildSchemas = childSchemas.sort(sortBySortOrder);

    const sortedProperties: Record<string, Schema> = {};

    for (const [index, childSchema] of sortedChildSchemas.entries()) {
      if (childSchema.metadata && childSchema.type) {
        const identifier = childSchema.metadata?.['payaca:identifier'];

        sortedProperties[identifier] = {
          ...childSchema,
          metadata: {
            ...childSchema.metadata,
            ['payaca:sort-index']: index + 1,
          },
        };
      }
    }

    super({
      ...schema,
      properties: {
        ...schema.properties,
        ...sortedProperties,
      },
    });
  }

  static fromInput(input: FieldsetCustomFieldDefinitionInput, depth = 1) {
    if (input.type !== 'fieldset') throw new Error('Invalid type');
    if (depth > MAX_FIELDSET_DEPTH) {
      throw new Error(
        `Fieldsets cannot be nested more than ${MAX_FIELDSET_DEPTH} levels deep`
      );
    }

    const properties: Record<string, Schema> = {};

    for (const [index, child] of input.children.entries() || []) {
      let childSchema: Schema;
      if (child.type === 'fieldset') {
        childSchema = FieldsetCustomFieldDefinition.fromInput(
          child,
          depth + 1
        ).schema;
      } else {
        childSchema = CustomFieldDefinition.fromInput(child).schema;
      }
      childSchema.metadata['payaca:sort-index'] = index + 1;

      properties[child.identifier] = childSchema;
    }

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties,
      additionalProperties: false,
    };

    return new FieldsetCustomFieldDefinition(schema);
  }

  get children(): AnyCustomFieldDefinition[] {
    const children = Object.values(
      this.schema.properties as Record<string, Schema>
    );
    const keys = Object.keys(this.schema.properties as Record<string, Schema>);

    if (!children?.length)
      throw new Error('Children are required for fieldset');

    const duplicateKeys = keys.filter(
      (item, index) => keys.indexOf(item) !== index
    );

    if (duplicateKeys.length) {
      throw new Error(
        `Duplicate keys found in fieldset: ${duplicateKeys.join(', ')}`
      );
    }

    return children
      .sort(sortBySortOrder)
      .map((child) => CustomFieldDefinition.fromSchema(child));
  }

  /**
   * Every child with its full path, recursing into nested fieldsets. Paths always
   * start with this fieldset's identifier, e.g. `['projectDetails', 'address',
   * 'street']`.
   */
  getChildrenWithPaths(): ChildWithPath[] {
    const rootPath = [this.identifier];

    const collectChildren = (
      children: AnyCustomFieldDefinition[],
      parentPath: string[]
    ): ChildWithPath[] => {
      return children.flatMap((child) => {
        const childPath = [...parentPath, child.identifier];
        const result: ChildWithPath[] = [
          {
            definition: child,
            path: childPath,
          },
        ];

        if (child instanceof FieldsetCustomFieldDefinition) {
          result.push(...collectChildren(child.children, childPath));
        }

        return result;
      });
    };

    return collectChildren(this.children, rootPath);
  }

  /**
   * Finds a child by its full path segments (including root fieldset identifier).
   * @param path - Full path segments, e.g., ['rootFieldset', 'childField'] or ['rootFieldset', 'nestedFieldset', 'childField']
   */
  findChildByPath(path: string[]): AnyCustomFieldDefinition | undefined {
    return this.getChildrenWithPaths().find(
      (c) =>
        c.path.length === path.length && c.path.every((p, i) => p === path[i])
    )?.definition;
  }

  defaultFormatter(value: CustomFieldHelperType['fieldset']['value']) {
    return JSON.stringify(value);
  }

  equals(
    _a: CustomFieldHelperType['fieldset']['value'] | null,
    _b: CustomFieldHelperType['fieldset']['value'] | null
  ): boolean {
    // Each child has its own equals semantics (e.g. string
    // `caseSensitive`/`trim`) that a structural compare would silently ignore.
    throw new Error(
      'FieldsetCustomFieldDefinition#equals is not implemented; resolve to a leaf field and compare there'
    );
  }
}

export class UploadCustomFieldDefinition extends CustomFieldDefinition<'upload'> {
  getExampleValue() {
    return [
      {
        name: 'cat.jpg',
        mimeType: 'image/jpeg',
        // Not realistic, but it resolves to a real image.
        url: 'https://placecats.com/900/600',
        thumbnailUrl: 'https://placecats.com/300/200',
      },
    ];
  }

  static fromInput(input: UploadCustomFieldDefinitionInput) {
    if (input.type !== 'upload') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'array' : ['array', 'null'],
      items: {
        type: 'object',
        properties: {
          url: {
            type: 'string',
          },
          name: {
            type: 'string',
          },
          mimeType: {
            type: 'string',
          },
          thumbnailUrl: {
            type: ['string', 'null'],
          },
        },
      },
      maxItems: input.limit || 1,
      minItems: input.isRequired ? 1 : 0,
    };

    // Categories, not the extensions they currently expand to, so a field saved
    // today accepts a file type added to that category tomorrow.
    schema.metadata['payaca:accepted-file-categories'] = input.uploadType
      ?.length
      ? input.uploadType
      : (['image'] satisfies UploadCategory[]);

    return new UploadCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['upload']['value']) {
    if (value.length > 0) {
      return `${value.length} file(s) uploaded: ${value
        .map((u) => u.url)
        .join(', ')}`;
    }

    return `0 files uploaded`;
  }

  get limit(): number {
    const limit = this.schema?.maxItems;

    return isNullish(limit) ? 1 : (limit as number);
  }

  get uploadTypes(): UploadCategory[] {
    const storedCategories =
      this.schema?.metadata?.['payaca:accepted-file-categories'];

    if (!isNullish(storedCategories)) {
      return storedCategories as UploadCategory[];
    }

    // Written before categories were persisted: recover them from the
    // extensions the field was saved with.
    const storedExtensions = (this.schema?.metadata?.[
      'payaca:accepted-file-extensions'
    ] || []) as string[];

    return categoriesFromLegacyExtensions(storedExtensions);
  }

  /**
   * The extensions this field accepts today, expanded from its categories — so
   * a field configured before a file type was supported still accepts it.
   */
  get acceptedFileExtensions(): string[] {
    return extensionsForCategories(this.uploadTypes);
  }

  equals(
    _a: CustomFieldHelperType['upload']['value'] | null,
    _b: CustomFieldHelperType['upload']['value'] | null
  ): boolean {
    throw new Error(
      'UploadCustomFieldDefinition#equals is not implemented; upload equality semantics will land alongside the first concrete caller'
    );
  }
}

export class UserCustomFieldDefinition extends CustomFieldDefinition<'user'> {
  getExampleValue() {
    return {
      userId: 'user_123',
    };
  }

  static fromInput(input: SimpleCustomFieldDefinitionInput) {
    if (input.type !== 'user') throw new Error('Invalid type');

    const schema: Schema = {
      ...this.getSharedSchemaPropertiesFromInput(input),
      type: input.isRequired ? 'object' : ['object', 'null'],
      properties: {
        userId: {
          type: ['string'],
        },
      },
    };

    return new UserCustomFieldDefinition(schema);
  }

  defaultFormatter(value: CustomFieldHelperType['user']['value']) {
    if (value?.userId?.length) {
      return `User ${value.userId}`;
    }
    return '';
  }

  equals(
    a: CustomFieldHelperType['user']['value'] | null,
    b: CustomFieldHelperType['user']['value'] | null
  ): boolean {
    if (a === null || b === null) return a === b;
    return a.userId === b.userId;
  }
}
